import { useMemo } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { LoaderCircle, Play, Send, Settings, Square, Star } from 'lucide-react';
import { z } from 'zod';

import { useGetAllCustomViewsByDashboard } from '@/api/gen/data-harmonization-v2-custom-views';
import { useGetAllDashboards, useGetDashboardExecutions } from '@/api/gen/data-harmonization-v2-dashboards';
import { TLCustomViewStatus, TLDashboardType, TLDataCheckExecutionStatus } from '@/api/gen/tessera-backend-api.schemas';
import { AddGraphMenuButton } from '@/components/features/data-harmonization/dashboard/analytics/AddGraphMenuButton';
import { AnalyticsGraphGrid } from '@/components/features/data-harmonization/dashboard/analytics/AnalyticsGraphGrid';
import { FilterBarToggleButton } from '@/components/features/data-harmonization/dashboard/analytics/FilterBarToggleButton';
import { CustomViewTabs } from '@/components/features/data-harmonization/dashboard/views/CustomViewTabs';
import { Chip } from '@/features/data-harmonization/Chip';
import { DASHBOARD_TABS_CONFIG } from '@/features/data-harmonization/config/dashboard-detail-tabs.config';
import { DataChecksTabContent } from '@/features/data-harmonization/dashboard/DataChecksTabContent';
import { ErrorTrackingTabContent } from '@/features/data-harmonization/dashboard/errorTracking/ErrorTrackingTabContent';
import { DashboardGraphGrid } from '@/features/data-harmonization/dashboard/graphs/DashboardGraphGrid';
import { DownloadGraphsButton } from '@/features/data-harmonization/dashboard/graphs/DownloadGraphsButton';
import { EditLayoutButton } from '@/features/data-harmonization/dashboard/graphs/EditLayoutButton';
import { CancelBatchExecutionConfirmationModal } from '@/features/data-harmonization/dashboard/modal/CancelBatchExecutionConfirmationModal';
import { DashboardPreferencesModal } from '@/features/data-harmonization/dashboard/modal/DashboardPreferencesModal';
import { DashboardShareModal } from '@/features/data-harmonization/dashboard/modal/DashboardShareModal';
import { ExecuteDataChecksConfirmationModal } from '@/features/data-harmonization/dashboard/modal/ExecuteDataChecksConfirmationModal';
import { DHTabsLayout } from '@/features/data-harmonization/DHTabsLayout';
import { useActiveBatchExecution } from '@/features/data-harmonization/hooks/useActiveBatchExecution';
import { useDashboardQuery } from '@/features/data-harmonization/hooks/useDashboardQuery';
import { useDHPermissions } from '@/features/data-harmonization/hooks/useDHPermissions';
import { useGetWorkstreamOptions } from '@/features/data-harmonization/hooks/useGetWorkstreamOptions';
import { useTabsConfig } from '@/features/data-harmonization/hooks/useTabsConfig';
import { DashboardLayoutStoreProvider } from '@/features/data-harmonization/providers/DashboardLayoutStoreProvider';
import { DHPageAccessProvider } from '@/features/data-harmonization/providers/DHPageAccessProvider';
import {
  TLAIDataHarmonizationDashboardTabType,
  TLAIDataHarmonizationLandingTabType,
} from '@/features/data-harmonization/types';
import { PageHeaderContent } from '@/features/page-layout/PageHeaderContent';
import { useLocalize } from '@/hooks/use-localize';
import { useModal } from '@/hooks/use-modal';
import { useUserPreference } from '@/hooks/use-user-preference';
import { Separator } from '@/modus/Separator';
import type { TLAIDataHarmonizationDashboard } from '@/types/api';
import { UserPreferenceKey } from '@/types/user-preferences';
import { Button } from '@/ui/Button';
import { formatLocalDate } from '@/utils/date';
import { cn } from '@/utils/styles';

export const Route = createFileRoute('/_protected/t/$tenantId/data-harmonization/dashboard/$dashboardId/')({
  component: DataHarmonizationDashboardRoute,
  validateSearch: z.object({
    tab: z.nativeEnum(TLAIDataHarmonizationDashboardTabType).optional().catch(undefined),
    view: z.string().optional().catch(undefined),
    filters: z.string().optional(),
    sorts: z.string().optional(),
  }),
});

const DASHBOARD_DETAIL_PAGE_PERMISSION = 'page:dashboards';

function DataHarmonizationDashboardRoute() {
  const router = useRouter();
  const { dashboardId, tenantId } = Route.useParams();
  const { tab: tabParam } = Route.useSearch();
  const { can } = useDHPermissions();
  const showViews = can('view:dashboard-views', dashboardId);
  const canEditViews = can('action:edit-dashboard-views', dashboardId);

  const { data: dashboard, isLoading: isLoadingDashboard } = useDashboardQuery(dashboardId, {
    enabled: can(DASHBOARD_DETAIL_PAGE_PERMISSION, dashboardId),
  });
  // undefined while loading — keeps harmonization request timing unchanged and only
  // parks the execution-data machinery once the dashboard is known to be ANALYTICS.
  const isAnalytics = dashboard?.dashboard_type === TLDashboardType.ANALYTICS;

  const { data: customViews, isLoading: isLoadingViews } = useGetAllCustomViewsByDashboard(
    { dashboardId },
    { status: canEditViews ? null : TLCustomViewStatus.ACTIVE },
    {
      query: { enabled: showViews && !isAnalytics },
    },
  );

  const hasActiveView = customViews?.views?.some((view) => view.status === TLCustomViewStatus.ACTIVE) ?? false;

  const { tLocalize } = useLocalize();
  const { enabledTabs, hasTabAccess, getActiveTab } = useTabsConfig({
    config: DASHBOARD_TABS_CONFIG,
    defaultTab: TLAIDataHarmonizationDashboardTabType.PROGRESS_OVERVIEW,
    dashboardId,
    hideViews: (customViews?.views?.length ?? 0) === 0 && !canEditViews,
    hideErrorTracking: hasActiveView,
  });

  const {
    open: isExecuteDataChecksModalOpen,
    handleOpen: handleExecuteDataChecksModalOpen,
    handleClose: handleExecuteDataChecksModalClose,
  } = useModal();

  const { open: isShareModalOpen, handleOpen: handleShareModalOpen, handleClose: handleShareModalClose } = useModal();

  const {
    open: isPreferencesModalOpen,
    handleOpen: handlePreferencesModalOpen,
    handleOpenChange: handlePreferencesModalOpenChange,
  } = useModal();

  const {
    open: isCancelBatchExecutionModalOpen,
    handleOpen: handleCancelBatchExecutionModalOpen,
    handleClose: handleCancelBatchExecutionModalClose,
  } = useModal();

  const canExecuteDataChecks = can('action:execute-data-checks', dashboardId);
  const { activeBatchExecution, isLoading: isLoadingBatchExecutions } = useActiveBatchExecution(dashboardId, {
    enabled: canExecuteDataChecks && !isAnalytics,
  });

  const activeTab = getActiveTab(tabParam ?? null);

  const handleTabChange = (value: TLAIDataHarmonizationDashboardTabType) => {
    router.navigate({
      to: '/t/$tenantId/data-harmonization/dashboard/$dashboardId',
      params: { tenantId, dashboardId },
      // Keep the selected custom view across tab switches; filters/sorts intentionally
      // reset because both tabs read them and stale ones would leak across tables.
      search: (prev) => ({ tab: value, view: prev.view }),
    });
  };

  const { data: executions } = useGetDashboardExecutions(
    { dashboardId },
    { limit: 1 },
    { query: { enabled: can(DASHBOARD_DETAIL_PAGE_PERMISSION, dashboardId) && !isAnalytics, retry: false } },
  );

  const { data: allDashboardsData } = useGetAllDashboards(undefined, {
    query: { enabled: can(DASHBOARD_DETAIL_PAGE_PERMISSION, dashboardId) },
  });
  const workstreamOptions = useGetWorkstreamOptions(allDashboardsData?.dashboards ?? []);

  const [favorites, { setValue: setFavorites, removeValues: removeFavorites }] = useUserPreference(
    UserPreferenceKey.DH_FAVORITE_DASHBOARD_IDS,
    [],
  );
  const isFavorite = favorites.includes(dashboardId);

  const handleToggleFavorite = async () => {
    if (isFavorite) {
      await removeFavorites([dashboardId], { successToast: 'Removed from favorites' });
    } else {
      await setFavorites([...favorites, dashboardId], { successToast: 'Added to favorites' });
    }
  };

  const lastExecutionDate = useMemo(() => {
    const lastCompletedExecution = executions?.executions
      ?.toSorted((a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())
      .find((execution) => execution.status === TLDataCheckExecutionStatus.COMPLETED);

    return lastCompletedExecution
      ? formatLocalDate(lastCompletedExecution.created_date, 'full', '-', 'en-US', true)
      : null;
  }, [executions?.executions]);

  const lastExecutionText = lastExecutionDate ? (
    <>
      <span className="pr-1 hidden lg:inline-block">{tLocalize('data-harmonization.dashboard.lastExecution')}</span>
      <span>{lastExecutionDate}</span>
    </>
  ) : (
    tLocalize('data-harmonization.dashboard.noExecutions')
  );

  return (
    <DHPageAccessProvider
      permission={DASHBOARD_DETAIL_PAGE_PERMISSION}
      isLoading={isLoadingDashboard || isLoadingViews}
      dashboardId={dashboardId}
    >
      {!dashboard ? (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <h1 className="text-2xl font-bold text-white">{tLocalize('data-harmonization.dashboard.notFoundTitle')}</h1>
          <p className="text-white/60">
            {tLocalize('data-harmonization.dashboard.notFoundDescription', { dashboardId })}
          </p>
          <Button
            size="sm"
            onClick={() =>
              router.navigate({
                to: '/t/$tenantId/data-harmonization',
                params: { tenantId },
                search: { tab: TLAIDataHarmonizationLandingTabType.DASHBOARDS },
              })
            }
          >
            {tLocalize('common.goBack')}
          </Button>
        </div>
      ) : (
        <DashboardLayoutStoreProvider>
          <PageHeaderContent>
            <div className="ml-auto flex items-center gap-2">
              <div className="flex shrink-0 items-center gap-2">
                {!isAnalytics && <span className="text-xs text-white/50">{lastExecutionText}</span>}
                {!isAnalytics &&
                  canExecuteDataChecks &&
                  (activeBatchExecution ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="group flex w-48 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 p-2 hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400"
                      tooltipContent="Cancel batch execution"
                      onClick={handleCancelBatchExecutionModalOpen}
                    >
                      <span className="flex items-center gap-2 group-hover:hidden group-focus-visible:hidden">
                        <LoaderCircle className="size-4 animate-spin" />
                        Executing...
                      </span>
                      <span className="hidden items-center gap-2 group-hover:flex group-focus-visible:flex">
                        <Square className="size-4" />
                        Cancel
                      </span>
                    </Button>
                  ) : (
                    <Button
                      Icon={Play}
                      iconProps={{ className: 'size-4' }}
                      size="sm"
                      variant="outline"
                      loading={isLoadingBatchExecutions}
                      className="flex w-48 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 p-2 hover:bg-white/10"
                      tooltipContent={tLocalize('data-harmonization.dashboard.executeAllTooltip')}
                      onClick={handleExecuteDataChecksModalOpen}
                    >
                      {tLocalize('data-harmonization.dashboard.executeAllButton')}
                    </Button>
                  ))}

                {can('action:favorite-dashboard', dashboardId) && (
                  <Button
                    Icon={Star}
                    iconProps={{ className: cn('size-4', isFavorite && 'fill-white') }}
                    size="sm"
                    variant="outline"
                    className="flex shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 p-2 hover:bg-white/10"
                    tooltipContent={
                      isFavorite
                        ? tLocalize('data-harmonization.dashboard.removeFromFavorites')
                        : tLocalize('data-harmonization.dashboard.addToFavorites')
                    }
                    onClick={handleToggleFavorite}
                  />
                )}
                <EditLayoutButton dashboardId={dashboardId} />
                <DownloadGraphsButton dashboardId={dashboardId} />

                {can('action:share-dashboard', dashboardId) && (
                  <>
                    <Separator orientation="vertical" className="bg-white/10" />
                    <Button
                      Icon={Send}
                      size="sm"
                      variant="outline"
                      className="flex shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 p-2 hover:bg-white/10"
                      tooltipContent={tLocalize('data-harmonization.dashboard.shareTooltip')}
                      onClick={handleShareModalOpen}
                    />
                  </>
                )}
                {can('view:dashboard-preferences', dashboardId) && (
                  <Button
                    Icon={Settings}
                    size="sm"
                    variant="outline"
                    className="flex shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 p-2 hover:bg-white/10"
                    tooltipContent={tLocalize('data-harmonization.dashboard.preferencesTooltip')}
                    aria-label="Dashboard Preferences"
                    onClick={handlePreferencesModalOpen}
                  />
                )}
              </div>
            </div>
          </PageHeaderContent>
          <div data-print-root="dh-dashboard">
            <div className="flex flex-col items-start justify-between">
              <div className="mb-4 w-full flex items-start justify-between">
                <div className="flex flex-col">
                  <div className="flex items-center gap-3">
                    <h1 className="text-xl font-bold text-[#D7DADC]">{dashboard?.name}</h1>
                    {dashboard?.workstream && <Chip value={dashboard?.workstream} className="w-fit justify-end" />}
                  </div>
                  <p className="max-w-4xl text-sm font-normal text-white/70">{dashboard?.description}</p>
                </div>

                {/* Harmonization only renders a graph grid on Progress Overview, so both
                    controls would be inert on the other tabs. */}
                {(isAnalytics || activeTab === TLAIDataHarmonizationDashboardTabType.PROGRESS_OVERVIEW) && (
                  <div className="flex shrink-0 items-center gap-2 print:hidden">
                    <AddGraphMenuButton dashboardId={dashboardId} />
                    <FilterBarToggleButton dashboardId={dashboardId} />
                  </div>
                )}
              </div>
            </div>
            {isAnalytics ? (
              <AnalyticsGraphGrid
                dashboardId={dashboardId}
                targetScore={dashboard.target_score ?? 0}
                baselineScore={dashboard.baseline_score ?? 0}
              />
            ) : (
              <DHTabsLayout
                activeTab={activeTab}
                onTabChange={handleTabChange}
                enabledTabs={enabledTabs}
                hasTabAccess={hasTabAccess}
                listClassName="mb-4"
              >
                <DHTabsLayout.Content value={TLAIDataHarmonizationDashboardTabType.PROGRESS_OVERVIEW}>
                  <DashboardGraphGrid
                    dashboardId={dashboardId}
                    targetScore={dashboard.target_score ?? 0}
                    baselineScore={dashboard.baseline_score ?? 0}
                    collapsibleFilters
                  />
                </DHTabsLayout.Content>

                <DHTabsLayout.Content value={TLAIDataHarmonizationDashboardTabType.DASHBOARD_VIEWS}>
                  <CustomViewTabs dashboardId={dashboardId} views={customViews?.views || []} />
                </DHTabsLayout.Content>
                <DHTabsLayout.Content value={TLAIDataHarmonizationDashboardTabType.ERROR_TRACKING}>
                  <ErrorTrackingTabContent dashboardId={dashboardId} />
                </DHTabsLayout.Content>
                <DHTabsLayout.Content value={TLAIDataHarmonizationDashboardTabType.DATA_CHECKS}>
                  <DataChecksTabContent dashboardId={dashboardId} dashboardName={dashboard?.name ?? ''} />
                </DHTabsLayout.Content>
              </DHTabsLayout>
            )}
          </div>
          {!isAnalytics && (
            <ExecuteDataChecksConfirmationModal
              open={isExecuteDataChecksModalOpen}
              dashboardId={dashboardId}
              onClose={handleExecuteDataChecksModalClose}
            />
          )}
          {isCancelBatchExecutionModalOpen && activeBatchExecution && (
            <CancelBatchExecutionConfirmationModal
              open={isCancelBatchExecutionModalOpen}
              dashboardId={dashboardId}
              batchExecutionId={activeBatchExecution.execution_id}
              onClose={handleCancelBatchExecutionModalClose}
            />
          )}
          <DashboardShareModal
            open={isShareModalOpen}
            onOpenChange={(open) => !open && handleShareModalClose()}
            dashboardId={dashboardId}
          />
          {isPreferencesModalOpen && (
            <DashboardPreferencesModal
              open={isPreferencesModalOpen}
              onOpenChange={handlePreferencesModalOpenChange}
              dashboard={dashboard as unknown as TLAIDataHarmonizationDashboard}
              workstreamOptions={workstreamOptions}
            />
          )}
        </DashboardLayoutStoreProvider>
      )}
    </DHPageAccessProvider>
  );
}
